import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle, Box, ChevronDown, ChevronRight, Clapperboard, Code2, Download, FileArchive, FileCode2, FileText, Globe, ImageIcon, Loader2, Mic, Pencil, RotateCcw, ShieldAlert, Sparkles, ThumbsDown, ThumbsUp, X,
} from "lucide-react";
import Markdown, { CopyButton } from "@/components/vai/Markdown";
import { KIND_LABELS, modelViewerDoc, splitArtifacts } from "@/components/vai/artifacts";
import { MODEL_INFO } from "@/lib/vai";

const TOOL_LABELS = {
  web_search: ["Recherche sur le web", Globe],
  fetch_page: ["Lecture d'une page", FileText],
  generate_image: ["Création d'une image", ImageIcon],
  generate_video: ["Création d'une vidéo", Clapperboard],
  text_to_speech: ["Création de la voix", Mic],
  edit_video: ["Montage de la vidéo", Clapperboard],
  create_3d_model: ["Création du modèle 3D", Box],
};

export function VantaMark({ className = "w-6 h-6" }) {
  return <img src="/vanta-ai-mark.webp" alt="" className={`${className} shrink-0 rounded-[24%] object-cover`} draggable={false} />;
}

function ArtifactCard({ part, onOpen, active }) {
  return (
    <button type="button" onClick={() => onOpen(part.id)} data-testid="vai-artifact-card"
      className={`my-3 w-full max-w-md flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? "border-blue-400/50 bg-blue-500/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}>
      <span className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
        {!part.complete ? <Loader2 className="w-5 h-5 text-white/70 animate-spin" /> : part.kind === "project" ? <FileArchive className="w-5 h-5 text-sky-300" /> : <Code2 className="w-5 h-5 text-white/70" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium truncate">{part.title}</span>
        <span className="block text-xs text-white/45">{part.complete ? `${KIND_LABELS[part.kind]} · cliquer pour ouvrir` : "Écriture en cours…"}</span>
      </span>
    </button>
  );
}

function Lightbox({ url, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <img src={url} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
      <div className="absolute top-4 right-4 flex gap-2">
        <a href={url} download target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/10 hover:bg-white/20" onClick={(e) => e.stopPropagation()} aria-label="Télécharger"><Download className="w-5 h-5" /></a>
        <button type="button" className="p-2 rounded-lg bg-white/10 hover:bg-white/20" onClick={onClose} aria-label="Fermer"><X className="w-5 h-5" /></button>
      </div>
    </div>
  );
}

function Media({ item }) {
  const [zoom, setZoom] = useState(false);
  if (item.kind === "image") {
    return (
      <>
        <button type="button" onClick={() => setZoom(true)} className="block overflow-hidden rounded-xl border border-white/10 bg-white/5 hover:opacity-95" data-testid="vai-media-image">
          <img src={item.url} alt="Création de Vanta AI" className="max-h-80 w-auto object-contain" loading="lazy" />
        </button>
        {zoom && <Lightbox url={item.url} onClose={() => setZoom(false)} />}
      </>
    );
  }
  if (item.kind === "video") return <video src={item.url} controls className="max-h-80 w-full max-w-lg rounded-xl border border-white/10 bg-black" data-testid="vai-media-video" />;
  if (item.kind === "audio") return <audio src={item.url} controls className="w-full max-w-md" data-testid="vai-media-audio" />;
  if (item.kind === "model3d") {
    return (
      <div className="w-full max-w-lg">
        <iframe title="Modèle 3D" srcDoc={modelViewerDoc(item.url)} sandbox="allow-scripts" className="w-full h-72 rounded-xl border border-white/10 bg-[#15151c]" />
        <a href={item.url} download className="mt-1.5 inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"><Download className="w-3.5 h-3.5" />Télécharger le .glb (Blender, Unity, Unreal…)</a>
      </div>
    );
  }
  return null;
}

function ToolBlock({ block, running }) {
  const [open, setOpen] = useState(false);
  const [label, Icon] = TOOL_LABELS[block.name] || ["Outil", Globe];
  const failed = block.status === "error" || block.status === "refused";
  const detail = block.input?.query || block.input?.url || block.input?.prompt || block.input?.name || block.input?.text || "";
  return (
    <div className="my-2" data-testid="vai-tool">
      <button type="button" onClick={() => setOpen(!open)} className="group inline-flex max-w-full items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-xs text-white/60 hover:text-white">
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : block.status === "refused" ? <ShieldAlert className="w-3.5 h-3.5 text-amber-300" /> : failed ? <AlertTriangle className="w-3.5 h-3.5 text-red-300" /> : <Icon className="w-3.5 h-3.5" />}
        <span className="font-medium">{running ? `${label}…` : label}</span>
        {detail && <span className="truncate max-w-[18rem] text-white/40">{String(detail)}</span>}
        {(block.sources?.length > 0 || failed) && (open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
      </button>
      {open && failed && <p className="mt-1.5 text-xs text-red-200/80">{block.error}</p>}
      {open && block.sources?.length > 0 && (
        <ul className="mt-2 space-y-1 pl-1">
          {block.sources.map((s, i) => (
            <li key={i} className="text-xs truncate">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:underline">{s.title || s.url}</a>
              <span className="text-white/30 ml-2">{(() => { try { return new URL(s.url).hostname; } catch { return ""; } })()}</span>
            </li>
          ))}
        </ul>
      )}
      {block.media?.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{block.media.map((m, i) => <Media key={i} item={m} />)}</div>}
    </div>
  );
}

export function UserMessage({ message, onEdit, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  if (editing) {
    return (
      <div className="flex justify-end">
        <div className="w-full max-w-2xl rounded-2xl border border-white/15 bg-white/[0.04] p-3">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={Math.min(10, draft.split("\n").length + 1)} className="w-full resize-none bg-transparent text-[15px] outline-none" autoFocus />
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" className="px-3 py-1.5 text-sm rounded-lg hover:bg-white/10" onClick={() => { setEditing(false); setDraft(message.content); }}>Annuler</button>
            <button type="button" className="px-3 py-1.5 text-sm rounded-lg bg-white text-black font-medium disabled:opacity-40" disabled={!draft.trim()} onClick={() => { setEditing(false); onEdit(message, draft.trim()); }}>Envoyer</button>
          </div>
        </div>
      </div>
    );
  }
  const files = message.attachments || [];
  return (
    <div className="group flex flex-col items-end" data-testid="vai-user-message">
      {files.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2 mb-2 max-w-[85%]">
          {files.map((f) => {
            const Icon = { zip: FileArchive, image: ImageIcon }[f.kind] || FileCode2;
            return (
              <span key={f.id || f.name} className="inline-flex items-center gap-2 h-10 pl-2 pr-3 rounded-xl border border-white/10 bg-white/[0.04] text-xs max-w-[240px]" data-testid="vai-user-attachment">
                <span className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><Icon className="w-3.5 h-3.5 text-sky-300" /></span>
                <span className="min-w-0"><span className="block truncate text-white/85">{f.name}</span><span className="block truncate text-[10px] text-white/40">{f.note || { zip: "Projet .zip", image: "Photo" }[f.kind] || "Fichier"}</span></span>
              </span>
            );
          })}
        </div>
      )}
      <div className="max-w-[85%] rounded-2xl bg-white/[0.07] border border-white/[0.06] px-4 py-2.5 text-[15px] whitespace-pre-wrap break-words">{message.content}</div>
      <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-white/40">
        <CopyButton text={message.content} className="px-1.5 py-1 rounded hover:bg-white/10 hover:text-white" />
        {!disabled && <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/10 hover:text-white"><Pencil className="w-3.5 h-3.5" />Modifier</button>}
      </div>
    </div>
  );
}

// En attendant le premier mot : « Vanta réfléchit… » avec le temps écoulé. Sur le
// processeur du serveur, le premier message peut prendre jusqu'à une minute
// (chargement du modèle) : on le dit plutôt que de laisser croire à une panne.
// Texte qui arrive par morceaux : affiché lettre par lettre à un rythme régulier
// (plus vite s'il y a du retard), comme une écriture continue.
function useSmoothText(text, active) {
  const [shown, setShown] = useState(active ? 0 : text.length);
  const target = useRef(text);
  target.current = text;
  useEffect(() => {
    if (!active) { setShown(text.length); return undefined; }
    let frame;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(100, now - last);
      last = now;
      setShown((n) => {
        const backlog = target.current.length - n;
        if (backlog <= 0) return n;
        const speed = Math.max(120, backlog * 8); // lettres par seconde : rattrape le retard en ~1/8 s
        return Math.min(target.current.length, n + Math.max(1, Math.round((speed * dt) / 1000)));
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  return active ? text.slice(0, shown) : text;
}

const CURSOR = "▍";
const EFFORT_NAMES = { minimal: "Minimal", moyen: "Moyen", eleve: "Élevé", tres_eleve: "Très élevé", ultra: "Ultra" };

function TextBlock({ text, streaming, onOpenArtifact, activeArtifact }) {
  const smooth = useSmoothText(text, streaming);
  // Curseur au bout de la dernière ligne pendant l'écriture (pas dans un bloc de code ouvert).
  const inCode = (smooth.match(/```/g) || []).length % 2 === 1;
  const shown = streaming && !inCode ? `${smooth}${CURSOR}` : smooth;
  return splitArtifacts(shown).map((p, j) => (p.type === "artifact"
    ? <ArtifactCard key={j} part={p} onOpen={onOpenArtifact} active={activeArtifact === p.id} />
    : <Markdown key={j} text={p.text} />));
}

// Réflexion de Vector (niveaux Très élevé et Ultra) : visible en direct pendant qu'il réfléchit,
// puis repliée au-dessus de la réponse, comme sur claude.ai.
function ThinkingBlock({ text, live }) {
  const [open, setOpen] = useState(false);
  const shown = open || live;
  return (
    <div className="mb-3" data-testid="vai-thinking-block">
      <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80">
        <Sparkles className="w-3.5 h-3.5 text-violet-300" />
        <span className={live ? "vai-shimmer font-medium" : ""}>{live ? "Vector réfléchit…" : "Réflexion"}</span>
        {!live && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {shown && <p className="mt-2 pl-3 border-l-2 border-violet-400/30 text-[13px] leading-relaxed text-white/50 whitespace-pre-wrap">{text}</p>}
    </div>
  );
}

function Thinking({ model }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="h-7 flex items-center gap-2 text-sm text-white/50" data-testid="vai-thinking">
      <span className="vai-shimmer font-medium">{MODEL_INFO[model]?.name || "Vector"} réfléchit…</span>
      {seconds >= 3 && <span className="text-xs text-white/30 tabular-nums">{seconds} s</span>}
      {seconds >= 12 && <span className="hidden sm:inline text-xs text-white/30">· le premier message peut prendre jusqu'à une minute</span>}
    </div>
  );
}

// 👍 / 👎 : l'échange noté part dans Panel admin > Vanta AI > Entraînement, où il sert à
// corriger Vanta. 👎 demande ce qui n'allait pas (facultatif).
const RATE_HINT = "Cet échange sera envoyé à l'équipe VantaPulse pour améliorer Vector.";

function RateButtons({ message, onRate }) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");
  const btn = "p-1.5 rounded hover:bg-white/10 hover:text-white";
  const rate = (rating, extra = "") => { setAsking(false); setNote(""); onRate(message, rating, extra); };
  return (
    <>
      <button type="button" onClick={() => rate(message.rating === "up" ? null : "up")} className={`${btn} ${message.rating === "up" ? "text-emerald-300" : ""}`}
        aria-label="Bonne réponse" aria-pressed={message.rating === "up"} title={`Bonne réponse. ${RATE_HINT}`} data-testid="vai-rate-up">
        <ThumbsUp className={`w-3.5 h-3.5 ${message.rating === "up" ? "fill-current" : ""}`} />
      </button>
      <button type="button" onClick={() => (message.rating === "down" ? rate(null) : setAsking(!asking))} className={`${btn} ${message.rating === "down" ? "text-red-300" : ""}`}
        aria-label="Mauvaise réponse" aria-pressed={message.rating === "down"} title={`Mauvaise réponse. ${RATE_HINT}`} data-testid="vai-rate-down">
        <ThumbsDown className={`w-3.5 h-3.5 ${message.rating === "down" ? "fill-current" : ""}`} />
      </button>
      {asking && (
        <div className="order-last basis-full mt-2 max-w-xl rounded-xl border border-white/10 bg-[#16161e] p-3" data-testid="vai-rate-form">
          <p className="text-sm text-white/80">Qu'est-ce qui n'allait pas ?</p>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} autoFocus placeholder="Facultatif : réponse fausse, incomplète, mauvais ton…"
            className="mt-2 w-full resize-none rounded-lg bg-black/20 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-white/25" />
          <p className="mt-1 text-[11px] text-white/35">{RATE_HINT}</p>
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => setAsking(false)} className="px-3 py-1.5 text-xs rounded-lg hover:bg-white/10 text-white/70">Annuler</button>
            <button type="button" onClick={() => rate("down", note.trim())} className="px-3 py-1.5 text-xs rounded-lg bg-white text-black font-medium" data-testid="vai-rate-send">Envoyer</button>
          </div>
        </div>
      )}
    </>
  );
}

export function AssistantMessage({ message, streaming, runningTool, onOpenArtifact, activeArtifact, onRetry, onRate, isLast }) {
  const text = (message.blocks || []).filter((b) => b.type === "text").map((b) => b.text).join("");
  const empty = !(message.blocks || []).length && streaming;
  const rateable = !String(message.id).startsWith("tmp") && (message.blocks || []).length > 0;
  return (
    <div className="group" data-testid="vai-assistant-message">
      <div className="min-w-0">
        {empty && !runningTool && <Thinking model={message.model} />}
        {(message.blocks || []).map((b, i, all) => (b.type === "tool"
          ? <ToolBlock key={i} block={b} />
          : b.type === "thinking" ? <ThinkingBlock key={i} text={b.text} live={streaming && i === all.length - 1} />
          : <TextBlock key={i} text={b.text} streaming={streaming && i === all.length - 1 && !runningTool}
              onOpenArtifact={onOpenArtifact} activeArtifact={activeArtifact} />))}
        {runningTool && <ToolBlock block={{ name: runningTool.name, input: {} }} running />}
        {message.pending && (
          <p className="mt-2 flex items-center gap-2 text-sm text-white/55" data-testid="vai-pending">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />Création en cours… le résultat s'affichera ici tout seul.
          </p>
        )}
        {message.error && <p className="mt-2 flex items-center gap-1.5 text-sm text-red-300"><AlertTriangle className="w-4 h-4 shrink-0" />{message.error}</p>}
        {message.stopped && <p className="h-7 flex items-center text-sm text-white/45">{(message.blocks || []).length ? "Réponse arrêtée." : "Tu as arrêté la réponse avant qu'elle commence."}</p>}
        {!streaming && (
          <div className={`flex flex-wrap items-center gap-1 mt-2 text-xs text-white/40 transition-opacity ${isLast || message.rating ? "" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"}`}>
            {text && <CopyButton text={text} className="px-1.5 py-1 rounded hover:bg-white/10 hover:text-white" />}
            {onRate && rateable && <RateButtons message={message} onRate={onRate} />}
            {isLast && onRetry && <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/10 hover:text-white" data-testid="vai-retry"><RotateCcw className="w-3.5 h-3.5" />Réessayer</button>}
            {message.model && <span className="ml-1">{MODEL_INFO[message.model]?.name || "Vector"}{message.effort ? ` · ${EFFORT_NAMES[message.effort] || ""}` : ""}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
